import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileUploader, buildFileUrl } from "@/components/common/FileUploader";
import { toast } from "sonner";
import {
  ArrowLeft,
  KeyRound,
  PauseCircle,
  PlayCircle,
  User,
  UserPlus,
  Users,
  Shield,
  GraduationCap,
  Phone,
  Mail,
  MapPin,
  Calendar,
} from "lucide-react";

const DOCUMENT_FIELDS = [
  ["doc_birth_certificate", "Birth Certificate"],
  ["doc_aadhaar_card", "Aadhaar Card"],
  ["doc_prev_marksheet", "Previous Marksheet"],
  ["doc_transfer_certificate", "Transfer Certificate (TC)"],
  ["doc_migration_certificate", "Migration Certificate"],
  ["doc_hostel_affidavit", "Hostel Affidavit"],
  ["doc_grade10_marksheet", "Grade 10 Marksheet"],
  ["doc_bank_passbook", "Bank Passbook Copy"],
  ["doc_address_proof", "Address Proof"],
  ["doc_vaccination_certificate", "Vaccination Certificate"],
  ["doc_student_photo", "Student Photo"],
  ["doc_father_photo", "Father Photo"],
  ["doc_mother_photo", "Mother Photo"],
];

function currency(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

function fullName(s) {
  return `${s?.first_name || ""} ${s?.middle_name || ""} ${s?.last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();
}

function extractFileId(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const v = value.trim();
    if (!v) return "";
    if (
      (v.startsWith("{") && v.endsWith("}")) ||
      (v.startsWith("[") && v.endsWith("]"))
    ) {
      try {
        return extractFileId(JSON.parse(v));
      } catch {
        return v;
      }
    }
    return v;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = extractFileId(item);
      if (found) return found;
    }
    return "";
  }
  if (typeof value === "object") {
    const candidate = value.id || value.file_id || value.photo_url || value.url;
    return candidate ? String(candidate) : "";
  }
  return "";
}

function isUploadedDocument(value) {
  return extractFileId(value) !== "";
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-2 text-sm py-1.5 border-b border-slate-100 last:border-b-0">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-800 break-words">{value || "—"}</div>
    </div>
  );
}

function ProfileCard({ title, icon: Icon, children, accent = "sky" }) {
  const accents = {
    sky: "bg-sky-50 text-sky-700 border-sky-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex w-8 h-8 items-center justify-center rounded-lg border ${accents[accent] || accents.sky}`}
        >
          <Icon className="w-4 h-4" />
        </span>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { branchId, sessionId } = useAuth();

  const [student, setStudent] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [disableDate, setDisableDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [disableReason, setDisableReason] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginMobile, setLoginMobile] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    const params = {
      branch_id: branchId || undefined,
      session_id: sessionId || undefined,
    };
    const [s, l, a] = await Promise.all([
      api.get(`/students/${id}`),
      api.get(`/fees/student-ledger/${id}`, { params }),
      api.get(`/attendance/student/${id}`, { params }),
    ]);
    setStudent(s.data || null);
    setLedger(l.data || null);
    setAttendanceRows(a.data?.items || []);
    setLoginEmail(s.data?.email || "");
    setLoginMobile(s.data?.mobile || "");
  }, [id, branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const attendanceSummary = useMemo(() => {
    const total = attendanceRows.length;
    const present = attendanceRows.filter(
      (r) => String(r.status || "").toUpperCase() === "P",
    ).length;
    const absent = attendanceRows.filter(
      (r) => String(r.status || "").toUpperCase() === "A",
    ).length;
    const pct = total > 0 ? ((present / total) * 100).toFixed(1) : "0.0";
    return { total, present, absent, pct };
  }, [attendanceRows]);

  const photoId = useMemo(() => {
    return (
      extractFileId(student?.doc_student_photo) ||
      extractFileId(student?.student_photo) ||
      extractFileId(student?.photo_url) ||
      extractFileId(student?.photo)
    );
  }, [student]);

  const photoUrl = photoId ? buildFileUrl(photoId) : "";

  const saveStudentField = async (key, value) => {
    setBusy(true);
    try {
      await api.put(`/students/${id}`, { [key]: value || "" });
      setStudent((prev) => ({ ...(prev || {}), [key]: value || "" }));
      toast.success("Updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/students/${id}/reset-password`, {
        new_password: newPassword.trim(),
      });
      setNewPassword("");
      toast.success("Student password reset successful");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Password reset failed");
    } finally {
      setBusy(false);
    }
  };

  const createLogin = async () => {
    if (loginPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!loginEmail.trim() && !loginMobile.trim()) {
      toast.error("Email or mobile is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/students/${id}/create-login`, {
        email: loginEmail.trim(),
        mobile: loginMobile.trim(),
        password: loginPassword.trim(),
      });
      setLoginPassword("");
      toast.success("Login account created");
      // Trust this response over a fresh GET /students/:id — it reflects the
      // login exactly as just created, whereas has_login_account on reload
      // depends on the student's email/mobile matching it (which the backend
      // now keeps in sync, but don't make the UI depend on a round trip for it).
      if (data?.student) {
        setStudent(data.student);
      } else {
        setStudent((prev) => ({ ...(prev || {}), has_login_account: true }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create login");
    } finally {
      setBusy(false);
    }
  };

  const disableStudent = async () => {
    if (!disableDate.trim()) {
      toast.error("Disable date is required");
      return;
    }
    if (!disableReason.trim()) {
      toast.error("Disable reason is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/students/${id}/disable`, {
        disabled_date: disableDate,
        disable_reason: disableReason.trim(),
      });
      toast.success("Student disabled");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disable failed");
    } finally {
      setBusy(false);
    }
  };

  const reactivateStudent = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${id}/reactivate`);
      toast.success("Student reactivated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reactivation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!student) {
    return (
      <div className="p-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-500">
        Loading student profile...
      </div>
    );
  }

  return (
    <div data-testid="student-profile-page">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-sky-50 via-white to-emerald-50 p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-xl overflow-hidden border border-slate-200 bg-white flex items-center justify-center text-[10px] text-slate-400">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Student"
                  className="w-full h-full object-cover"
                />
              ) : (
                "NO PHOTO"
              )}
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 mb-3"
                onClick={() => nav("/students")}
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Students
              </Button>
              <h1 className="text-2xl font-bold text-slate-800">
                {fullName(student) || "Unnamed Student"}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Admission No: {student.admission_no || "—"} | Class:{" "}
                {ledger?.class_label || "—"} | Section:{" "}
                {ledger?.section || student.section || "—"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${String(student.status || "").toLowerCase() === "disabled" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}
            >
              {String(student.status || "active").toUpperCase()}
            </span>
            <div className="mt-2 text-xs text-slate-500">
              Gender: {student.gender || "—"}
            </div>
            <div className="text-xs text-slate-500">
              Mobile: {student.mobile || "—"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Ledger Balance</div>
          <div className="text-xl font-bold text-slate-800">
            {currency(ledger?.ledger?.balance)}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Total Debit</div>
          <div className="text-xl font-bold text-slate-800">
            {currency(ledger?.ledger?.total_dr)}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Total Credit</div>
          <div className="text-xl font-bold text-slate-800">
            {currency(ledger?.ledger?.total_cr)}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">Attendance</div>
          <div className="text-xl font-bold text-slate-800">
            {attendanceSummary.pct}%
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-3 h-9">
          <TabsTrigger value="overview">Profile</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="ledger">Student Ledger</TabsTrigger>
          <TabsTrigger value="attendance">Attendance Card</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <ProfileCard title="Basic" icon={User} accent="sky">
              <InfoRow label="Admission No" value={student.admission_no} />
              <InfoRow label="Student Name" value={fullName(student)} />
              <InfoRow label="Gender" value={student.gender} />
              <InfoRow
                label="Class"
                value={`${ledger?.class_label || student.class_id || "—"}${ledger?.section || student.section ? ` / ${ledger?.section || student.section}` : ""}`}
              />
              <InfoRow
                label="DOB"
                value={student.date_of_birth || student.dob}
              />
              <InfoRow label="Mobile" value={student.mobile} />
              <InfoRow label="Email" value={student.email} />
              <InfoRow
                label="Address"
                value={
                  student.address ||
                  [student.addr_line1, student.city, student.state, student.pin]
                    .filter(Boolean)
                    .join(", ")
                }
              />
            </ProfileCard>

            <ProfileCard title="Father" icon={Users} accent="emerald">
              <InfoRow
                label="Name"
                value={
                  student.father_name ||
                  [
                    student.father_first_name,
                    student.father_middle_name,
                    student.father_last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
              />
              <InfoRow label="Mobile" value={student.father_mobile} />
              <InfoRow label="Email" value={student.father_email} />
              <InfoRow label="Occupation" value={student.father_occupation} />
              <InfoRow
                label="Qualification"
                value={student.father_qualification}
              />
            </ProfileCard>

            <ProfileCard title="Mother" icon={Users} accent="rose">
              <InfoRow
                label="Name"
                value={
                  student.mother_name ||
                  [
                    student.mother_first_name,
                    student.mother_middle_name,
                    student.mother_last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
              />
              <InfoRow label="Mobile" value={student.mother_mobile} />
              <InfoRow label="Email" value={student.mother_email} />
              <InfoRow label="Occupation" value={student.mother_occupation} />
              <InfoRow
                label="Qualification"
                value={student.mother_qualification}
              />
            </ProfileCard>

            <ProfileCard title="Guardian" icon={Shield} accent="violet">
              <InfoRow label="Type" value={student.guardian_type} />
              <InfoRow
                label="Name"
                value={
                  student.guardian_name ||
                  [student.guardian_first_name, student.guardian_last_name]
                    .filter(Boolean)
                    .join(" ")
                }
              />
              <InfoRow label="Relation" value={student.guardian_relation} />
              <InfoRow label="Mobile" value={student.guardian_mobile} />
              <InfoRow label="Email" value={student.guardian_email} />
              <InfoRow label="Address" value={student.guardian_address} />
            </ProfileCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <GraduationCap className="w-4 h-4" />
                <span className="text-sm font-semibold">Academic</span>
              </div>
              <div className="text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Session</span>
                  <span>{student.session_id || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Roll No</span>
                  <span>{student.roll_no || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span>{student.status || "—"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <Phone className="w-4 h-4" />
                <span className="text-sm font-semibold">Contact</span>
              </div>
              <div className="text-sm space-y-1.5">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  {student.mobile || "—"}
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  {student.email || "—"}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  {[student.city, student.state].filter(Boolean).join(", ") ||
                    "—"}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  Attendance Snapshot
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 py-2.5">
                  <div className="text-[11px] text-emerald-700">Present</div>
                  <div className="text-base font-bold text-emerald-800">
                    {attendanceSummary.present}
                  </div>
                </div>
                <div className="rounded-lg bg-rose-50 py-2.5">
                  <div className="text-[11px] text-rose-700">Absent</div>
                  <div className="text-base font-bold text-rose-800">
                    {attendanceSummary.absent}
                  </div>
                </div>
                <div className="rounded-lg bg-sky-50 py-2.5">
                  <div className="text-[11px] text-sky-700">Total</div>
                  <div className="text-base font-bold text-sky-800">
                    {attendanceSummary.total}
                  </div>
                </div>
              </div>
              {String(student.status || "").toLowerCase() === "disabled" && (
                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
                  Disabled On: {student.disabled_date || "—"} | Reason:{" "}
                  {student.disable_reason || "—"}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-0">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3 text-sm font-semibold text-slate-800">
              Documents
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {DOCUMENT_FIELDS.map(([key, label]) => (
                <div
                  key={key}
                  className="rounded-md border border-slate-200 p-2"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-slate-700">
                      {label}
                    </div>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${isUploadedDocument(student?.[key]) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      {isUploadedDocument(student?.[key])
                        ? "Uploaded"
                        : "Not Uploaded"}
                    </span>
                  </div>
                  <FileUploader
                    label=""
                    value={student?.[key]}
                    onChange={(v) => saveStudentField(key, v)}
                    tag={key}
                    testid={`profile-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-0">
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 text-slate-700 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Voucher</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Narration</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(ledger?.ledger?.rows || []).map((r, idx) => (
                  <tr
                    key={`${r.voucher_no}-${idx}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2">{r.date || "—"}</td>
                    <td className="px-3 py-2">{r.voucher_no || "—"}</td>
                    <td className="px-3 py-2">{r.voucher_type || "—"}</td>
                    <td className="px-3 py-2">{r.narration || "—"}</td>
                    <td className="px-3 py-2 text-right">{currency(r.dr)}</td>
                    <td className="px-3 py-2 text-right">{currency(r.cr)}</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {currency(r.balance)}
                    </td>
                  </tr>
                ))}
                {(ledger?.ledger?.rows || []).length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-8 text-center text-slate-400"
                    >
                      No ledger entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-0">
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="bg-slate-50 text-slate-700 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Class</th>
                  <th className="px-3 py-2 text-left">Section</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {attendanceRows.map((r, idx) => (
                  <tr
                    key={`${r.date}-${idx}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2">{r.date || "—"}</td>
                    <td className="px-3 py-2">{r.class_id || "—"}</td>
                    <td className="px-3 py-2">
                      {r.section || r.section_id || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${String(r.status || "").toUpperCase() === "A" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}
                      >
                        {String(r.status || "P").toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
                {attendanceRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-8 text-center text-slate-400"
                    >
                      No attendance records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="actions" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              {student.has_login_account === false ? (
                <>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">
                    Create Login
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">
                    This student doesn't have a login account yet (admitted
                    without one). Create it here, same as during admission.
                  </p>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs font-semibold">Email</Label>
                      <Input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="student@example.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Mobile</Label>
                      <Input
                        value={loginMobile}
                        onChange={(e) => setLoginMobile(e.target.value)}
                        placeholder="10-digit mobile"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">
                        Password
                      </Label>
                      <Input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Minimum 6 characters"
                      />
                    </div>
                    <Button
                      className="erp-primary"
                      onClick={createLogin}
                      disabled={busy}
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" /> Create Login
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">
                    Reset Password
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">
                      New Password
                    </Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 characters"
                    />
                    <Button
                      className="erp-primary"
                      onClick={resetPassword}
                      disabled={busy}
                    >
                      <KeyRound className="w-4 h-4 mr-1.5" /> Reset Password
                    </Button>
                  </div>
                </>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">
                Student Status
              </h3>
              {String(student.status || "").toLowerCase() === "disabled" ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2">
                    Student is currently disabled.
                  </p>
                  <Button
                    variant="outline"
                    onClick={reactivateStudent}
                    disabled={busy}
                  >
                    <PlayCircle className="w-4 h-4 mr-1.5" /> Reactivate Student
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs font-semibold">
                      Disable Date
                    </Label>
                    <Input
                      type="date"
                      value={disableDate}
                      onChange={(e) => setDisableDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">
                      Disable Reason
                    </Label>
                    <Input
                      value={disableReason}
                      onChange={(e) => setDisableReason(e.target.value)}
                      placeholder="Reason is required"
                    />
                  </div>
                  <Button
                    variant="destructive"
                    onClick={disableStudent}
                    disabled={busy}
                  >
                    <PauseCircle className="w-4 h-4 mr-1.5" /> Disable Student
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
